/**
 * DefaultRangeStrategy
 * Handles all timeline ranges that don't have specific content
 * Shows a message indicating no data is available for the selected range
 */

import type { ContentStrategy, PeriodType, TabName } from '@/types';
import { DOM_SELECTORS } from '@/utils/constants';
import {
  removeSections,
  hideKeyStatsContent,
  showNoDataMessage,
  injectContent
} from './ContentHelper';

/**
 * Default strategy for timeline ranges with no specific content
 */
export class DefaultRangeStrategy implements ContentStrategy {
  /**
   * Apply strategy: show message for unsupported timeline range
   */
  public apply(tab: TabName, _periodType: PeriodType): void {
    // Remove all financial statement sections that might have been injected
    removeSections([
      DOM_SELECTORS.CAP_SECTION_2020_2023,
      DOM_SELECTORS.VAL_SECTION_2020_2023,
      DOM_SELECTORS.INCOME_SECTION_2020_2023,
      DOM_SELECTORS.INCOME_SECTION_1925,
      DOM_SELECTORS.INCOME_SECTION_1323,
      DOM_SELECTORS.BALANCE_SECTION_2020_2023,
      DOM_SELECTORS.BALANCE_SECTION_1925,
      DOM_SELECTORS.BALANCE_SECTION_1323,
      DOM_SELECTORS.CASH_SECTION_2020_2023,
      DOM_SELECTORS.CASH_SECTION_1925,
      DOM_SELECTORS.CASH_SECTION_1323,
    ]);

    // For Key Stats tab - hide original content and show "No data" message
    if (tab === 'key-stats') {
      // Hide the original Key Stats content (don't destroy it)
      hideKeyStatsContent();

      // Show "No data available" message
      showNoDataMessage();
    } else {
      // For financial statement tabs (Income, Balance, Cash) - inject message
      // These tabs don't have pre-rendered content in main-section to preserve
      const messageHTML = `
        <div style="display: flex; align-items: center; justify-content: center; min-height: 400px; width: 100%;">
          <p style="font-size: 16px; color: #666; text-align: center; font-family: 'Times New Roman', Times, serif;">
            No data available for this timeline range.
          </p>
        </div>
      `;

      injectContent(DOM_SELECTORS.MAIN_SECTION, messageHTML);
    }
  }
}
